// ── Task chip typeahead: Depends on (TPT598, TPT9213) and the New Task Parent picker ──
//
// Candidates are queried from the server as the user types — one bounded summary page of
// GET /tasks per query (lib/parent-picker.js taskSearchPath(), the contract the drawer's Change
// Parent picker uses) — never read off a caller's task rows. A board holds only its filtered
// page (TPT597), so a list-backed picker would silently hide every task outside the filter.
//
// Used for the `dependencies` field of both task forms (New Task modal, task drawer / page) and,
// with `max: 1`, for the New Task modal's Parent. Each chip shows what is known about its task:
// its title and status from the search row it was picked from, or from a dependency check
// (GET /tasks/dependency-check) the host passes to setInfo(); a closed task is marked done, and a
// task the check rejected is marked with the reason (lib/task-fields.js dependencyProblemText()).
//
// Same dropdown discipline as lib/tag-typeahead.js, and the same `.tag-typeahead*` classes so it
// shares that styling: body-appended `position: fixed` dropdown (a modal's overflow can't clip
// it), mousedown+preventDefault on rows, a guarded blur timer, capture-phase scroll/resize
// repositioning, and an Escape that stopPropagation()s while the dropdown is open so it never
// reaches openModal()'s document-level Escape.

import { api } from '../api.js';
import { escapeHtml, debounce } from '../utils.js';
import { taskSearchPath, dependencyCandidates, PARENT_PICKER_LIMIT } from '../lib/parent-picker.js';
import { dependencyProblemText } from '../lib/task-fields.js';
import { t } from '../lib/i18n.js';

function escapeAttr(s) {
  return escapeHtml(s).replace(/"/g, '&quot;');
}

const upper = key => String(key).toUpperCase();

// Inline glyphs: a check for a closed (satisfied) dependency, a warning triangle for one the
// dependency check rejected. currentColor, so each palette colours them through CSS.
const DONE_ICON = '<svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m3.5 8.5 3 3 6-7"/></svg>';
const PROBLEM_ICON = '<svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M8 2 1.8 13h12.4Z"/><path d="M8 6.5v3M8 11.5h0"/></svg>';

// What one chip shows. `entry` is the merged info for its key (may be undefined).
function chipState(entry) {
  if (entry && entry.problem) return 'problem';
  if (entry && entry.closed) return 'done';
  return 'open';
}

function chipTooltip(key, entry, statusText) {
  if (!entry) return key;
  if (entry.problem) return entry.message || dependencyProblemText(entry);
  const parts = [entry.title ? `${key} — ${entry.title}` : key];
  const status = entry.status ? statusText(entry.status) : '';
  if (status) parts.push(status);
  return parts.join(' · ');
}

// One chip's markup. `removable` false renders a read-only chip (viewers).
function chipHtml(key, entry, { removable = true, showTitle = false, statusText = s => s } = {}) {
  const state = chipState(entry);
  const icon = state === 'problem' ? PROBLEM_ICON : state === 'done' ? DONE_ICON : '';
  const title = showTitle && entry && entry.title
    ? `<span class="dep-chip-title">${escapeHtml(entry.title)}</span>`
    : '';
  const done = state === 'done' ? `<span class="sr-only"> (${escapeHtml(t('deps.done'))})</span>` : '';
  return `<span class="tag tag-typeahead-chip dep-chip dep-chip--${state}" data-key="${escapeAttr(key)}" title="${escapeAttr(chipTooltip(key, entry, statusText))}">`
    + (icon ? `<span class="dep-chip-icon">${icon}</span>` : '')
    + `<span class="dep-chip-key">${escapeHtml(key)}</span>${title}${done}`
    + (removable ? `<span class="tag-typeahead-chip-remove" data-key="${escapeAttr(key)}" role="button" aria-label="${escapeAttr(`× ${key}`)}">&times;</span>` : '')
    + '</span>';
}

// Read-only chips for a viewer, from the same info a writer's chips use. `info` maps a key (any
// case) to a check result or search row. Empty → the localized "None".
export function renderDependencyChips(keys, info = new Map(), opts = {}) {
  const list = (keys || []).filter(k => typeof k === 'string' && k);
  if (list.length === 0) return `<span class="text-muted">${escapeHtml(t('taskField.none'))}</span>`;
  return list.map(key => chipHtml(key, info.get(upper(key)), { ...opts, removable: false })).join('');
}

// attachDependencyTypeahead(input, opts) — replaces `input` (a plain text <input>, already in
// the DOM) with a chip row wrapping it.
//
// opts:
//   projectId      number|string  required — scopes the task search
//   initialKeys    string[]       task keys to seed as chips (default [])
//   initialInfo    object[]       search rows / check results for those keys (titles, status…)
//   excludeKeys    string[]       keys never offered — the task itself, in the drawer
//   max            number         chip limit (default unlimited); 1 = single pick, where a new
//                                 pick replaces the chip and the input hides while one is set
//   showTitles     boolean        chip shows the task title beside its key (single pick)
//   statusText(s)  string         a status name's display label for chip tooltips
//   isClosed(s)    boolean        whether a status closes a task (done chip) — search rows carry
//                                 no closed flag, unlike check results
//   scrollEl       Element|null   scroll container to reposition the dropdown against
//   onChange(keys, change)        fired after a user add/remove; change = { added: row } |
//                                 { removed: key }
//   isAlive()      boolean        false once the host UI is gone (guards timers and replies)
//
// Returns { getKeys, getEntry, setInfo, setKeys, focus({ open }), close, destroy }. Keys are task_key
// strings, the `dependencies` format; getEntry(key) is the merged info for a key (search row
// fields — id, title, status, priority, type — plus closed/problem/message from a check).
export function attachDependencyTypeahead(input, opts = {}) {
  const noop = { getKeys: () => [], getEntry: () => undefined, setInfo() {}, setKeys() {}, focus() {}, close() {}, destroy() {} };
  if (!input) return noop;

  const { projectId, initialKeys = [], initialInfo = [], scrollEl = null, onChange } = opts;
  const max = Number.isFinite(opts.max) && opts.max > 0 ? opts.max : Infinity;
  const excluded = new Set((opts.excludeKeys || []).filter(Boolean).map(upper));
  const statusText = typeof opts.statusText === 'function' ? opts.statusText : (s => s);
  const isClosed = typeof opts.isClosed === 'function' ? opts.isClosed : () => false;
  const isAliveFn = typeof opts.isAlive === 'function' ? opts.isAlive : () => true;

  const wrapper = document.createElement('div');
  wrapper.className = 'tag-typeahead dep-typeahead';
  input.replaceWith(wrapper);

  const chipsHost = document.createElement('span');
  chipsHost.className = 'tag-typeahead-chips';
  wrapper.appendChild(chipsHost);
  wrapper.appendChild(input);
  input.classList.add('tag-typeahead-input');
  input.type = 'text';
  input.autocomplete = 'off';
  input.maxLength = 200; // the list route's q cap
  if (!input.placeholder) input.placeholder = t('deps.placeholder');

  let keys = [...new Set((initialKeys || []).filter(k => typeof k === 'string' && k))].slice(0, max);
  const info = new Map(); // UPPER key -> merged entry
  let dropdown = null;
  let rows = [];              // last reply's rows (already excluding chosen keys)
  let full = false;           // last reply filled a whole page
  let message = '';           // loading / error / empty text shown instead of rows
  let activeIndex = -1;
  let blurTimer = null;
  let seq = 0;
  let destroyed = false;
  // The input text the listed rows answer, and an Enter pressed before the reply for the current
  // text landed: typing a key and pressing Enter at once must pick from that key's results, never
  // from the list still showing for the text before it.
  let shownQuery = null;
  let enterPending = false;
  let quietFocus = false; // focus({ open: false }) — the next focus does not open the dropdown

  const alive = () => !destroyed && isAliveFn();

  function remember(row) {
    if (!row || !row.task_key) return;
    const prev = info.get(upper(row.task_key)) || {};
    const next = { ...prev, ...row };
    if (row.status !== undefined && row.closed === undefined) next.closed = isClosed(row.status);
    if (row.problem !== undefined) next.message = row.problem ? dependencyProblemText(row) : '';
    info.set(upper(row.task_key), next);
  }
  (initialInfo || []).forEach(remember);

  function fireChange(change) {
    if (typeof onChange === 'function') onChange(keys.slice(), change);
  }

  function renderChips() {
    chipsHost.innerHTML = keys.map(key => chipHtml(key, info.get(upper(key)), { showTitle: !!opts.showTitles, statusText })).join('');
    // A full single pick needs no search box until its chip is removed.
    input.hidden = max !== Infinity && keys.length >= max;
  }

  chipsHost.addEventListener('mousedown', (e) => {
    const btn = e.target.closest('.tag-typeahead-chip-remove');
    if (!btn) return;
    e.preventDefault(); // keep focus on the input
    const key = btn.dataset.key;
    keys = keys.filter(k => k !== key);
    renderChips();
    fireChange({ removed: key });
    if (dropdown) search();
    else if (input.hidden === false && max !== Infinity) input.focus();
  });

  function positionDropdown() {
    if (!dropdown) return;
    const r = wrapper.getBoundingClientRect();
    dropdown.style.top = `${r.bottom + 2}px`;
    dropdown.style.left = `${r.left}px`;
    dropdown.style.width = `${Math.max(r.width, 220)}px`;
  }

  const onScrollOrResize = () => positionDropdown();
  if (scrollEl) scrollEl.addEventListener('scroll', onScrollOrResize, true);
  window.addEventListener('resize', onScrollOrResize);

  function updateActive() {
    if (!dropdown) return;
    dropdown.querySelectorAll('.tag-typeahead-option').forEach((el, i) => {
      el.classList.toggle('active', i === activeIndex);
    });
  }

  function renderDropdown() {
    if (!dropdown) {
      dropdown = document.createElement('div');
      dropdown.className = 'tag-typeahead-dropdown';
      dropdown.addEventListener('mousedown', onDropdownMousedown);
      document.body.appendChild(dropdown);
    }
    if (activeIndex >= rows.length) activeIndex = rows.length ? 0 : -1;
    dropdown.innerHTML = message
      ? `<div class="tag-typeahead-empty">${escapeHtml(message)}</div>`
      : rows.map((row, i) => `<div class="tag-typeahead-option" data-index="${i}">${escapeHtml(row.task_key)}<small>${escapeHtml(row.title || '')}${row.status ? ` · ${escapeHtml(statusText(row.status))}` : ''}</small></div>`).join('')
        + (full ? `<div class="tag-typeahead-empty">Showing the first ${PARENT_PICKER_LIMIT} tasks — type to narrow.</div>` : '');
    updateActive();
    positionDropdown();
  }

  function hideDropdown() {
    seq++; // a reply still in flight must not reopen it
    if (dropdown) { dropdown.remove(); dropdown = null; }
    rows = [];
    activeIndex = -1;
    shownQuery = null;
    enterPending = false;
  }

  // One server query for the input's current text; a reply superseded by a newer query, or
  // landing after the dropdown closed or the host went away, is dropped.
  async function search() {
    if (!alive()) return;
    // A debounced call that fires after blur already closed the dropdown must not reopen it.
    if (!dropdown && document.activeElement !== input) return;
    const mySeq = ++seq;
    const query = input.value;
    if (!dropdown) { rows = []; message = 'Loading...'; full = false; renderDropdown(); }
    let reply;
    try {
      reply = await api.get(taskSearchPath(projectId, query));
    } catch (err) {
      if (mySeq !== seq || !alive() || !dropdown) return;
      rows = [];
      full = false;
      shownQuery = null;
      enterPending = false;
      message = (err && err.message) || 'Failed to load tasks';
      renderDropdown();
      return;
    }
    if (mySeq !== seq || !alive() || !dropdown) return;
    const page = (reply && reply.tasks) || [];
    full = page.length >= PARENT_PICKER_LIMIT;
    rows = dependencyCandidates(page, keys).filter(row => !excluded.has(upper(row.task_key)));
    message = rows.length || full ? '' : 'No matching tasks';
    activeIndex = rows.length ? 0 : -1;
    shownQuery = query;
    renderDropdown();
    if (enterPending && input.value === query) {
      enterPending = false;
      if (rows.length) commit(rows[0]);
    }
  }

  const debouncedSearch = debounce(search, 250);

  function commit(row) {
    if (!keys.includes(row.task_key)) {
      const removed = max !== Infinity && keys.length >= max ? keys.slice(max - 1) : [];
      keys = [...keys.slice(0, keys.length - removed.length), row.task_key];
      // A pick knows its title, sprint and status; a check may add the rest later.
      remember({ id: row.id, task_key: row.task_key, title: row.title || '', status: row.status, priority: row.priority, type: row.type, problem: null });
      renderChips();
      fireChange({ added: info.get(upper(row.task_key)) });
    }
    input.value = '';
    renderChips();
    if (max !== Infinity && keys.length >= max) { hideDropdown(); return; }
    search(); // stay open, refreshed without the key just added — continuous multi-add
  }

  function onDropdownMousedown(e) {
    const opt = e.target.closest('.tag-typeahead-option');
    e.preventDefault(); // keep focus on the input — never let a click on the dropdown blur it
    if (!opt) return;
    const row = rows[Number(opt.dataset.index)];
    if (row) commit(row);
  }

  input.addEventListener('focus', () => {
    if (blurTimer) { clearTimeout(blurTimer); blurTimer = null; }
    if (quietFocus) { quietFocus = false; return; }
    if (!dropdown) search();
  });

  input.addEventListener('click', () => { if (!dropdown) search(); });

  input.addEventListener('input', () => debouncedSearch());

  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!dropdown) { search(); return; }
      if (rows.length) { activeIndex = (activeIndex + 1) % rows.length; updateActive(); }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (dropdown && rows.length) { activeIndex = (activeIndex - 1 + rows.length) % rows.length; updateActive(); }
    } else if (e.key === 'Enter') {
      // Always prevented — this field sits inside the New Task <form>, where a bare Enter submits.
      e.preventDefault();
      if (dropdown && rows.length && activeIndex >= 0 && shownQuery === input.value) commit(rows[activeIndex]);
      else if (input.value.trim()) { enterPending = true; search(); } // pick once this text's reply lands
    } else if (e.key === 'Backspace' && !input.value && keys.length) {
      const removed = keys.pop();
      renderChips();
      fireChange({ removed });
      if (dropdown) search();
    } else if (e.key === 'Escape' && dropdown) {
      e.preventDefault();
      e.stopPropagation();
      hideDropdown();
    }
  });

  input.addEventListener('blur', () => {
    blurTimer = setTimeout(() => {
      blurTimer = null;
      if (!alive()) return;
      hideDropdown();
      input.value = '';
    }, 150);
  });

  function getKeys() { return keys.slice(); }

  function getEntry(key) { return info.get(upper(key)); }

  // Merges check results (or rows) into the chips' info and repaints them in place.
  function setInfo(entries) {
    if (destroyed) return;
    (entries || []).forEach(remember);
    renderChips();
  }

  // Replaces the chips without firing onChange (a host restoring a saved list).
  function setKeys(next) {
    if (destroyed) return;
    keys = [...new Set((next || []).filter(k => typeof k === 'string' && k))].slice(0, max);
    renderChips();
  }

  // `open: false` focuses without the result list — a host pointing at a problem it reported
  // under the field must not cover that report with the dropdown.
  function focus({ open = true } = {}) {
    if (destroyed || input.hidden) return;
    quietFocus = !open && document.activeElement !== input;
    input.focus();
  }

  function destroy() {
    destroyed = true;
    hideDropdown();
    if (blurTimer) { clearTimeout(blurTimer); blurTimer = null; }
    if (scrollEl) scrollEl.removeEventListener('scroll', onScrollOrResize, true);
    window.removeEventListener('resize', onScrollOrResize);
  }

  renderChips();
  return { getKeys, getEntry, setInfo, setKeys, focus, close: hideDropdown, destroy };
}
